import { Injectable, computed, signal } from '@angular/core';
import { coachesApi } from '../http/coaches.api';
import {
  CoachContract,
  CoachMarket,
  CoachOffer,
  CoachOfferDto,
  isCoachContractActive,
} from '../models/coaches.types';

@Injectable({
  providedIn: 'root',
})
export class CoachesState {
  private generation = 0;
  private requests = { market: 0, contracts: 0, offers: 0 };
  readonly offersLoaded = signal(false);
  readonly retryOffer = signal<{ marketId: string; coachId: string; offerId: string; dto: CoachOfferDto } | null>(null);
  readonly canSubmitNewOffer = computed(() => this.offersLoaded() && !this.offersError() &&
    !this.isLoadingOffers() && !this.isLoadingMarket() && !this.marketError() &&
    !this.isSubmittingOffer() && !this.isCancellingOffer() && !this.retryOffer() && this.pendingOffers().length === 0);

  matchesContext(clubId: string, groupId: string): boolean {
    const current = this.loadedContext();
    return current?.clubId === clubId && current?.groupId === groupId;
  }

  selectContext(clubId: string, groupId: string): void {
    if (this.matchesContext(clubId, groupId)) return;
    this.clearState();
    this.loadedContext.set({ clubId, groupId });
  }

  readonly market = signal<CoachMarket | null>(null);
  readonly contracts = signal<CoachContract[]>([]);
  readonly offers = signal<CoachOffer[]>([]);

  readonly isLoadingMarket = signal<boolean>(false);
  readonly isLoadingContracts = signal<boolean>(false);
  readonly isLoadingOffers = signal<boolean>(false);
  readonly isSubmittingOffer = signal<boolean>(false);
  readonly isCancellingOffer = signal<boolean>(false);

  readonly marketError = signal<string | null>(null);
  readonly contractsError = signal<string | null>(null);
  readonly offersError = signal<string | null>(null);

  /** Contexto actualmente cargado (clubId + groupId) */
  readonly loadedContext = signal<{ clubId: string; groupId: string } | null>(null);

  /** Contrato de entrenador actualmente vigente en el club */
  readonly activeContract = computed<CoachContract | null>(() => {
    const list = this.contracts();
    return list.find((c) => isCoachContractActive(c)) || null;
  });

  /** Indica si el club tiene un entrenador vigente contratado */
  readonly hasActiveCoach = computed<boolean>(() => this.activeContract() !== null);

  /** Lista de ofertas pendientes de resolución */
  readonly pendingOffers = computed<CoachOffer[]>(() => {
    return this.offers().filter((o) => (o.status || '').toUpperCase() === 'PENDING');
  });

  /** Diccionario de ofertas pendientes por coach_id */
  readonly pendingOffersByCoachId = computed<Record<string, CoachOffer>>(() => {
    const map: Record<string, CoachOffer> = {};
    for (const offer of this.pendingOffers()) {
      if (offer.coach_id) {
        map[offer.coach_id] = offer;
      }
    }
    return map;
  });

  /**
   * Resuelve el nombre visible de un entrenador a partir del mercado o contratos cargados.
   * Si no se encuentra (ej. oferta histórica antigua), devuelve una etiqueta neutra.
   * NUNCA consulta el catálogo ADMIN.
   */
  resolveCoachName(coachId: string): string {
    if (!coachId) return 'Entrenador';
    // 1. Buscar en listados del mercado actual
    const inMarket = this.market()?.listings?.find((l) => l.id === coachId);
    if (inMarket && inMarket.name) return inMarket.name;

    // 2. Buscar en histórico de contratos
    const inContracts = this.contracts().find((c) => c.coach_id === coachId);
    if (inContracts && inContracts.name) return inContracts.name;

    // 3. Etiqueta neutra
    return `Entrenador #${coachId.slice(0, 6)}`;
  }

  /**
   * Resuelve la imagen de un entrenador a partir del mercado o contratos cargados.
   */
  resolveCoachImage(coachId: string): string | null {
    if (!coachId) return null;
    const inMarket = this.market()?.listings?.find((l) => l.id === coachId);
    if (inMarket && inMarket.image_url) return inMarket.image_url;

    const inContracts = this.contracts().find((c) => c.coach_id === coachId);
    if (inContracts && inContracts.image_url) return inContracts.image_url;

    return null;
  }

  /**
   * Invalida la caché y resetea el estado ante cambios de club o de liga.
   */
  clearState(): void {
    this.generation++;
    this.offersLoaded.set(false);
    this.retryOffer.set(null);
    this.isLoadingMarket.set(false);
    this.isLoadingContracts.set(false);
    this.isLoadingOffers.set(false);
    this.isSubmittingOffer.set(false);
    this.isCancellingOffer.set(false);
    this.market.set(null);
    this.contracts.set([]);
    this.offers.set([]);
    this.marketError.set(null);
    this.contractsError.set(null);
    this.offersError.set(null);
    this.loadedContext.set(null);
  }

  /**
   * Carga el mercado diario de entrenadores
   */
  async loadMarket(clubId: string, groupId: string, force: boolean = false): Promise<CoachMarket | null> {
    this.selectContext(clubId, groupId);
    const generation = this.generation;
    const request = ++this.requests.market;
    const currentRequest = () => generation === this.generation && request === this.requests.market;
    const current = this.loadedContext();
    if (!force && !this.isLoadingMarket() && !this.marketError() && current?.clubId === clubId && current?.groupId === groupId && this.market()) {
      return this.market();
    }

    this.isLoadingMarket.set(true);
    this.marketError.set(null);
    try {
      const data = await coachesApi.getMarket(clubId, groupId);
      if (!currentRequest()) return null;
      this.market.set(data);
      this.loadedContext.set({ clubId, groupId });
      return data;
    } catch (err: any) {
      if (!currentRequest()) return null;
      const msg = this.extractErrorMessage(err, 'No se pudo cargar el mercado diario de entrenadores.');
      this.marketError.set(msg);
      return null;
    } finally {
      if (currentRequest()) this.isLoadingMarket.set(false);
    }
  }

  /**
   * Carga el historial de contratos del club
   */
  async loadContracts(clubId: string, groupId: string, force: boolean = false): Promise<CoachContract[]> {
    this.selectContext(clubId, groupId);
    const generation = this.generation;
    const request = ++this.requests.contracts;
    const currentRequest = () => generation === this.generation && request === this.requests.contracts;
    const current = this.loadedContext();
    if (!force && !this.isLoadingContracts() && !this.contractsError() && current?.clubId === clubId && current?.groupId === groupId && this.contracts().length > 0) {
      return this.contracts();
    }

    this.isLoadingContracts.set(true);
    this.contractsError.set(null);
    try {
      const list = await coachesApi.getContracts(clubId, groupId);
      if (!currentRequest()) return [];
      this.contracts.set(list);
      this.loadedContext.set({ clubId, groupId });
      return list;
    } catch (err: any) {
      if (!currentRequest()) return [];
      const msg = this.extractErrorMessage(err, 'No se pudo cargar el historial de contratos de entrenadores.');
      this.contractsError.set(msg);
      return [];
    } finally {
      if (currentRequest()) this.isLoadingContracts.set(false);
    }
  }

  /**
   * Carga las últimas 100 ofertas del club
   */
  async loadOffers(clubId: string, groupId: string, force: boolean = false): Promise<CoachOffer[]> {
    this.selectContext(clubId, groupId);
    const generation = this.generation;
    const request = ++this.requests.offers;
    const currentRequest = () => generation === this.generation && request === this.requests.offers;
    const current = this.loadedContext();
    if (!force && !this.isLoadingOffers() && !this.offersError() && current?.clubId === clubId && current?.groupId === groupId && this.offersLoaded()) {
      return this.offers();
    }

    this.isLoadingOffers.set(true);
    this.offersError.set(null);
    try {
      const list = await coachesApi.getOffers(clubId, groupId);
      if (!currentRequest()) return [];
      this.offers.set(list);
      this.offersLoaded.set(true);
      if (list.some(offer => offer.id === this.retryOffer()?.offerId)) this.retryOffer.set(null);
      this.loadedContext.set({ clubId, groupId });
      return list;
    } catch (err: any) {
      if (!currentRequest()) return [];
      const msg = this.extractErrorMessage(err, 'No se pudo cargar el historial de ofertas de entrenadores.');
      this.offersError.set(msg);
      return [];
    } finally {
      if (currentRequest()) this.isLoadingOffers.set(false);
    }
  }

  /**
   * Carga en paralelo mercado, contratos y ofertas
   */
  async loadAll(clubId: string, groupId: string, force: boolean = false): Promise<void> {
    await Promise.all([
      this.loadMarket(clubId, groupId, force),
      this.loadContracts(clubId, groupId, force),
      this.loadOffers(clubId, groupId, force),
    ]);
  }

  /**
   * Registra una oferta formal para un entrenador
   */
  async submitOffer(
    clubId: string,
    groupId: string,
    marketId: string,
    coachId: string,
    offerId: string,
    dto: CoachOfferDto
  ): Promise<void> {
    const retry = this.retryOffer();
    if (!this.matchesContext(clubId, groupId) || this.isSubmittingOffer() || this.isCancellingOffer()) {
      throw new Error('El contexto ha cambiado o hay una operación en curso.');
    }
    if (retry) {
      if (retry.offerId !== offerId || retry.marketId !== marketId || retry.coachId !== coachId ||
          JSON.stringify(retry.dto) !== JSON.stringify(dto)) throw new Error('Reintenta la oferta original antes de enviar otra.');
    } else if (!this.canSubmitNewOffer()) {
      throw new Error('Solo puedes tener una oferta de entrenador activa. Actualiza tus ofertas.');
    }
    const generation = this.generation;
    this.retryOffer.set({ marketId, coachId, offerId, dto: { ...dto } });
    this.isSubmittingOffer.set(true);
    try {
      await coachesApi.submitOffer(clubId, groupId, marketId, coachId, offerId, dto);
      if (generation !== this.generation) return;
      this.retryOffer.set(null);
      // Un 204 significa oferta registrada: refrescar ofertas y contratos
      await Promise.all([
        this.loadOffers(clubId, groupId, true),
        this.loadMarket(clubId, groupId, true),
      ]);
    } catch (err: any) {
      if (generation !== this.generation) throw err;
      if (err?.status >= 400 && err?.status < 500 && err?.status !== 408) this.retryOffer.set(null);
      // Si fue conflicto (ej. mercado cerrado o oferta duplicada), refrescar mercado y ofertas
      if (err?.status === 409) {
        await Promise.all([
          this.loadMarket(clubId, groupId, true),
          this.loadOffers(clubId, groupId, true),
        ]).catch(() => {});
      }
      throw err;
    } finally {
      if (generation === this.generation) this.isSubmittingOffer.set(false);
    }
  }

  /**
   * Cancela una oferta pendiente
   */
  async cancelOffer(clubId: string, groupId: string, offerId: string): Promise<void> {
    if (!this.matchesContext(clubId, groupId) || this.isSubmittingOffer() || this.isCancellingOffer()) {
      throw new Error('El contexto ha cambiado o hay una operación en curso.');
    }
    const generation = this.generation;
    this.isCancellingOffer.set(true);
    try {
      await coachesApi.cancelOffer(clubId, groupId, offerId);
      if (generation !== this.generation) return;
      // Refrescar ofertas
      await Promise.all([this.loadOffers(clubId, groupId, true), this.loadMarket(clubId, groupId, true)]);
    } catch (err: any) {
      if (generation !== this.generation) throw err;
      if (err?.status === 409) {
        await Promise.all([
          this.loadMarket(clubId, groupId, true),
          this.loadOffers(clubId, groupId, true),
        ]).catch(() => {});
      }
      throw err;
    } finally {
      if (generation === this.generation) this.isCancellingOffer.set(false);
    }
  }

  /**
   * Extrae el mensaje de error seguro devuelto por el servidor o un fallback
   */
  extractErrorMessage(err: any, fallback: string): string {
    if (!err) return fallback;
    if (err?.status === 401) return 'Sesión no válida o expirada. Por favor, inicia sesión de nuevo.';
    if (err?.status === 403) return 'Acceso denegado: solo el propietario del club miembro de la liga puede acceder a esta información.';
    if (err?.status === 404) return 'El recurso solicitado o el entrenador no está disponible en este momento.';
    if (err?.status === 409) return 'No se pudo completar la operación: puede haber una oferta activa o el mercado haber cerrado. Hemos actualizado tus ofertas; revísalas antes de continuar.';
    if (err?.status === 503) return 'Servicio de competición/entrenadores temporalmente no disponible.';
    return err?.message || err?.detail || fallback;
  }
}
